/**
 * Re-Sync Types
 * 
 * Type definitions for the Excel artifact re-sync system.
 * Used by ResyncModal, ResyncService, and related components.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SYNC STATUS
// ═══════════════════════════════════════════════════════════════════════════

/** Status of an artifact's sync state */
export type ArtifactSyncStatus = 
    | "up_to_date"      // Source unchanged since last sync
    | "outdated"        // Source modified, update available
    | "missing_file"    // Source file not found
    | "checking"        // Currently checking status
    | "error";          // Error during check

/** Presentation-friendly status labels used by the new Excel content UI */
export type ArtifactPresentationStatus =
    | "new"
    | "no_change"
    | "not_found"
    | "error";

// ═══════════════════════════════════════════════════════════════════════════
// SCAN TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Result of scanning the presentation */
export interface ScanResult {
    /** Total shapes scanned across all slides */
    totalShapes: number;
    /** Number of Carousel-inserted shapes found */
    carouselShapes: number;
    /** Detailed info for each artifact */
    artifacts: ArtifactScanItem[];
    /** Files that couldn't be found */
    missingFiles: MissingFileInfo[];
}

/** Individual artifact found during scan */
export interface ArtifactScanItem {
    // ─────────────────────────────────────────────────────────────────────────
    // Identity
    // ─────────────────────────────────────────────────────────────────────────
    
    /** PowerPoint shape ID */
    shapeId: string;
    /** 0-based slide index */
    slideIndex: number;
    /** Optional slide name/title */
    slideName?: string;
    /** Artifact ID (artifact_xxx format) */
    artifactId: string;
    /** Excel source ID (excel_xxx format) */
    excelId: string;
    
    // ─────────────────────────────────────────────────────────────────────────
    // Type
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Type of content: chart or range */
    artifactType: "chart" | "range";
    
    // ─────────────────────────────────────────────────────────────────────────
    // Source Info
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Source file and location info */
    source: {
        /** Path to the original Excel file */
        originalFilePath: string;
        /** Path to the clone file (if edit mode was clone) */
        cloneFilePath?: string;
        /** Sheet name where content was exported from */
        sheetName: string;
        /** Chart name or range reference (e.g., "Revenue Chart" or "A1:D10") */
        sourceRef: string;
    };
    
    // ─────────────────────────────────────────────────────────────────────────
    // Export Options (for re-export)
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Export options used when artifact was created */
    exportOptions: {
        blackAndWhite: boolean;
        collapseGroups: boolean;
        clearComments: boolean;
    };
    
    // ─────────────────────────────────────────────────────────────────────────
    // Status
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Current sync status */
    status: ArtifactSyncStatus;
    /** UI presentation status (Figma-aligned) */
    presentationStatus?: ArtifactPresentationStatus;
    /** Optional status detail for table/review messaging */
    statusReason?: string;
    /** When the artifact was last synced (ISO timestamp) */
    lastSyncedAt: string;
    /** When the source file was last modified (ISO timestamp, if checked) */
    sourceModifiedAt?: string;
    /** 1-based page for table display */
    page?: number;
    /** Most recent source timestamp for "Newest" column */
    newestAt?: string;
    
    // ─────────────────────────────────────────────────────────────────────────
    // Images for Comparison (populated after status check)
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Current shape image from PowerPoint (base64 PNG) */
    currentImageBase64?: string;
    /** Re-exported image from source (base64 PNG) */
    updatedImageBase64?: string;
    
    // ─────────────────────────────────────────────────────────────────────────
    // Error Info
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Error message if status check failed */
    error?: string;
    
    // ─────────────────────────────────────────────────────────────────────────
    // UI State
    // ─────────────────────────────────────────────────────────────────────────
    
    /** Whether user has selected this for batch update */
    selected?: boolean;
}

/** Info about a missing source file */
export interface MissingFileInfo {
    /** Original file path that couldn't be found */
    originalPath: string;
    /** Excel ID for this source */
    excelId: string;
    /** Shapes that depend on this file */
    affectedShapes: Array<{
        shapeId: string;
        slideIndex: number;
        artifactId: string;
        artifactType: "chart" | "range";
        sourceRef: string;
    }>;
    /** New path if user has relocated the file */
    relocatedPath?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// UPDATE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Result of updating a single artifact */
export interface UpdateResult {
    /** Whether the update succeeded */
    success: boolean;
    /** The artifact that was updated */
    artifactId: string;
    /** Original shape ID (before deletion) */
    shapeId?: string;
    /** New shape ID (after replacement) */
    newShapeId?: string;
    /** Error message if update failed */
    error?: string;
}

/** Result of batch update operation */
export interface BatchUpdateResult {
    /** Total artifacts attempted */
    total: number;
    /** Number that succeeded */
    succeeded: number;
    /** Number that failed */
    failed: number;
    /** Individual results */
    results: UpdateResult[];
}

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Phases of the scan/check process */
export type ScanPhase = 
    | "idle"
    | "scanning_slides"    // Finding Carousel shapes
    | "checking_status"    // Checking each artifact's status
    | "complete";

/** Scan progress state for UI updates */
export interface ScanProgress {
    /** Current phase */
    phase: ScanPhase;
    /** Current item being processed */
    current: number;
    /** Total items to process */
    total: number;
    /** Optional message for display */
    message?: string;
}

/** Update progress state */
export interface UpdateProgress {
    /** Current artifact being updated */
    current: number;
    /** Total artifacts to update */
    total: number;
    /** Current artifact ID being processed */
    currentArtifactId?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// API TYPES (for Python backend communication)
// ═══════════════════════════════════════════════════════════════════════════

/** Response from GET /excel/file/info */
export interface FileInfoResponse {
    /** Whether the file exists */
    exists: boolean;
    /** Last modified timestamp (ISO format) */
    lastModified: string | null;
    /** File size in bytes */
    sizeBytes: number | null;
    /** Error message if check failed */
    error: string | null;
}

/** Request body for POST /excel/artifact/regenerate */
export interface RegenerateArtifactRequest {
    /** Path to the Excel source file */
    source_file_path: string;
    /** Sheet name containing the content */
    sheet_name: string;
    /** Type of artifact */
    artifact_type: "chart" | "range";
    /** Chart name or range reference */
    source_ref: string;
    /** Export options to apply */
    export_options: {
        black_and_white: boolean;
        collapse_groups: boolean;
    };
}

/** Response from POST /excel/artifact/regenerate */
export interface RegenerateArtifactResponse {
    /** Whether regeneration succeeded */
    success: boolean;
    /** Base64 PNG image */
    image_base64?: string;
    /** Path to mini-workbook for OLE */
    ole_file_path?: string;
    /** Error message if failed */
    error?: string;
}

/** Request body for POST /excel/artifact/rescan-one */
export interface RescanOneArtifactRequest {
    source_file_path: string;
    sheet_name: string;
    artifact_type: "chart" | "range";
    source_ref: string;
    last_synced_at?: string;
    export_options?: {
        black_and_white: boolean;
        collapse_groups: boolean;
        clear_comments?: boolean;
    };
    force_regenerate?: boolean;
}

/** Response from POST /excel/artifact/rescan-one */
export interface RescanOneArtifactResponse {
    success: boolean;
    status: ArtifactPresentationStatus;
    source_last_modified: string | null;
    updated_image_base64?: string | null;
    ole_file_path?: string | null;
    error?: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Summary counts for UI display */
export interface SyncSummary {
    upToDate: number;
    outdated: number;
    missingFile: number;
    error: number;
    total: number;
}

/**
 * Calculate sync summary from artifacts
 */
export function calculateSyncSummary(artifacts: ArtifactScanItem[]): SyncSummary {
    return artifacts.reduce(
        (summary, artifact) => {
            summary.total++;
            switch (artifact.status) {
                case "up_to_date":
                    summary.upToDate++;
                    break;
                case "outdated":
                    summary.outdated++;
                    break;
                case "missing_file":
                    summary.missingFile++;
                    break;
                case "error":
                    summary.error++;
                    break;
            }
            return summary;
        },
        { upToDate: 0, outdated: 0, missingFile: 0, error: 0, total: 0 }
    );
}

/** Map internal status values to Figma-oriented presentation labels */
export function toPresentationStatus(status: ArtifactSyncStatus): ArtifactPresentationStatus {
    switch (status) {
        case "outdated":
            return "new";
        case "up_to_date":
            return "no_change";
        case "missing_file":
            return "not_found";
        case "error":
            return "error";
        case "checking":
        default:
            return "no_change";
    }
}








